import {Tabs, CustomItem} from '@jetbrains/ring-ui/components/tabs/tabs'
import {useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'

import {getTabsArg} from '../../../../rest/tabs'
import {getHrefWithQueryParams} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {getTabParamsKey, parseTabTitle} from '../../../../utils/tabs'
import OverviewTab, {renderTitle} from '../../../common/OverviewTab/OverviewTab'
import TabsLoader from '../../../common/TabsLoader/TabsLoader'
import {getChange} from '../Changes.selectors'

import BuildsTab from './BuildsTab/BuildsTab'
import type {ChangeTab, ChangeDetailsTabsProps} from './ChangeDetailsTabs.types'
import {ChangePageTabName, ChangePageTabTitle} from './ChangeDetailsTabs.types'
import {matchClassicToSakuraTabName} from './ChangeDetailsTabs.utils'
import DeploymentsTab from './DeploymentsTab/DeploymentsTab'
import FilesTab from './FilesTab/FilesTab'
import ProblemsTestsTab from './ProblemsTestsTab/ProblemsTestsTab'

import {useAppSelector} from 'src/hooks/react-redux'

function ChangeDetailsTabs({
  className,
  buildTypeId,
  changeId,
  currentTab,
  onSelectTab,
  withHref,
}: ChangeDetailsTabsProps) {
  const location = useLocation()

  const {personal = false} = useAppSelector(state => getChange(state, changeId)) ?? {}
  const tabParamsKey = getTabParamsKey({changeId, personal})

  const {ready, tabs} = restApi.endpoints.getTabs.useQuery(getTabsArg(tabParamsKey, true, false), {
    selectFromResult: ({data}) => ({ready: data != null, tabs: data ?? emptyArray}),
  })

  const getTabLink = useCallback(
    (tabId: string) =>
      getHrefWithQueryParams(location, null, params => ({...params, tab: stringifyId(tabId)})),
    [location],
  )

  const onSelect = useCallback(
    (tab: string) => onSelectTab(tab as ChangePageTabName),
    [onSelectTab],
  )

  const getTabsComponents = useCallback(
    (id: string) => {
      switch (id) {
        case ChangePageTabName.PROBLEMS_TESTS:
          return <ProblemsTestsTab changeId={changeId} />
        case ChangePageTabName.BUILDS:
          return <BuildsTab changeId={changeId} />
        case ChangePageTabName.DEPLOYMENTS:
          return <DeploymentsTab changeId={changeId} />
        case ChangePageTabName.FILES:
          return <FilesTab changeId={changeId} buildTypeId={buildTypeId} />

        default:
          return null
      }
    },
    [changeId, buildTypeId],
  )

  const renderTab = useCallback(
    (tab?: ChangeTab) => {
      let tabId = currentTab
      let titleText = ChangePageTabTitle[currentTab]
      let title = renderTitle(titleText)
      const tabPage = getTabsComponents(tabId)

      if (tab != null) {
        const {name, counter} = parseTabTitle(tab.title)
        tabId = matchClassicToSakuraTabName(tab.id)
        titleText = name
        title = renderTitle(name, counter)
      }

      return (
        <OverviewTab
          key={stringifyId(tabId)}
          id={stringifyId(tabId)}
          title={title}
          titleProps={{'aria-label': titleText}}
          href={withHref ? getTabLink(tabId) : undefined}
        >
          {tabPage}
        </OverviewTab>
      )
    },
    [currentTab, getTabLink, getTabsComponents, withHref],
  )

  const renderTabs = useMemo(
    () => (tabs.length > 0 ? tabs.map(renderTab) : [renderTab()]),
    [renderTab, tabs],
  )

  return (
    <Tabs
      className={className}
      key={`${stringifyId(changeId)}`}
      selected={currentTab}
      onSelect={onSelect}
    >
      {renderTabs}
      {!ready && (
        <CustomItem key="tabs_loader">
          <TabsLoader />
        </CustomItem>
      )}
    </Tabs>
  )
}

export default ChangeDetailsTabs
